import { createApi } from "@reduxjs/toolkit/query/react";
import { dynamicBaseQuery } from "@/api/DynamicBaseQuery";
import { APIEndpoints } from "@/constants/apiEndpoints";
import type { IApiResponse } from "@/types";
import type {
  IAssessment,
  IAssessmentAttempt,
  IAssessmentAttemptDetail,
  IAssessmentFinalDecisionPayload,
  IAssessmentLibraryCounts,
  IGenerateAssessmentPayload,
  IListAssessmentsParams,
  ISubmitAssessmentReviewPayload,
  IUpsertAssessmentPayload,
} from "@/types/(company)/recruitment-assessment.interface";

/**
 * Assessment library + candidate attempts.
 *
 * Anything that changes an attempt also invalidates `Assessment` LIST/COUNTS,
 * because the library's counts and a candidate's attempt list are two views of
 * the same records.
 */
export const recruitmentAssessmentsApi = createApi({
  reducerPath: "recruitmentAssessmentsApi",
  baseQuery: dynamicBaseQuery,
  tagTypes: ["Assessment", "AssessmentAttempt"],
  endpoints: (builder) => ({
    getAssessments: builder.query<IApiResponse<IAssessment[]>, IListAssessmentsParams | void>({
      query: (params) => ({ url: APIEndpoints.Recruitment.Assessments, params: params ?? {} }),
      providesTags: (result) =>
        result
          ? [
              ...result.data.map((a) => ({ type: "Assessment" as const, id: a.id })),
              { type: "Assessment" as const, id: "LIST" },
            ]
          : [{ type: "Assessment" as const, id: "LIST" }],
    }),

    getAssessmentCounts: builder.query<IApiResponse<IAssessmentLibraryCounts>, void>({
      query: () => ({ url: APIEndpoints.Recruitment.AssessmentCounts }),
      providesTags: [{ type: "Assessment", id: "COUNTS" }],
    }),

    getAssessment: builder.query<IApiResponse<IAssessment>, number>({
      query: (id) => ({ url: APIEndpoints.Recruitment.AssessmentById(id) }),
      providesTags: (_r, _e, id) => [{ type: "Assessment", id }],
    }),

    createAssessment: builder.mutation<
      IApiResponse<IAssessment>,
      { body: IUpsertAssessmentPayload; publish?: boolean }
    >({
      query: ({ body, publish }) => ({
        url: APIEndpoints.Recruitment.Assessments,
        method: "POST",
        params: publish ? { publish: "true" } : {},
        body,
      }),
      invalidatesTags: [
        { type: "Assessment", id: "LIST" },
        { type: "Assessment", id: "COUNTS" },
      ],
    }),

    updateAssessment: builder.mutation<
      IApiResponse<IAssessment>,
      { id: number; body: IUpsertAssessmentPayload; publish?: boolean }
    >({
      query: ({ id, body, publish }) => ({
        url: APIEndpoints.Recruitment.AssessmentById(id),
        method: "PUT",
        params: publish ? { publish: "true" } : {},
        body,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "Assessment", id },
        { type: "Assessment", id: "LIST" },
        { type: "Assessment", id: "COUNTS" },
      ],
    }),

    generateAssessment: builder.mutation<IApiResponse<IAssessment>, IGenerateAssessmentPayload>({
      query: (body) => ({
        url: APIEndpoints.Recruitment.AssessmentGenerate,
        method: "POST",
        body,
      }),
      invalidatesTags: [
        { type: "Assessment", id: "LIST" },
        { type: "Assessment", id: "COUNTS" },
      ],
    }),

    duplicateAssessment: builder.mutation<IApiResponse<IAssessment>, number>({
      query: (id) => ({
        url: APIEndpoints.Recruitment.AssessmentDuplicate(id),
        method: "POST",
      }),
      invalidatesTags: [
        { type: "Assessment", id: "LIST" },
        { type: "Assessment", id: "COUNTS" },
      ],
    }),

    archiveAssessment: builder.mutation<IApiResponse<IAssessment>, number>({
      query: (id) => ({
        url: APIEndpoints.Recruitment.AssessmentArchive(id),
        method: "POST",
      }),
      invalidatesTags: (_r, _e, id) => [
        { type: "Assessment", id },
        { type: "Assessment", id: "LIST" },
        { type: "Assessment", id: "COUNTS" },
      ],
    }),

    restoreAssessment: builder.mutation<IApiResponse<IAssessment>, number>({
      query: (id) => ({
        url: APIEndpoints.Recruitment.AssessmentRestore(id),
        method: "POST",
      }),
      invalidatesTags: (_r, _e, id) => [
        { type: "Assessment", id },
        { type: "Assessment", id: "LIST" },
        { type: "Assessment", id: "COUNTS" },
      ],
    }),

    // ── Attempts ────────────────────────────────────────────────────────────

    getCandidateAssessments: builder.query<IApiResponse<IAssessmentAttempt[]>, number>({
      query: (candidateId) => ({
        url: APIEndpoints.Recruitment.CandidateAssessments(candidateId),
      }),
      providesTags: (_r, _e, candidateId) => [
        { type: "AssessmentAttempt", id: `CANDIDATE_${candidateId}` },
      ],
    }),

    getAssessmentAttemptDetail: builder.query<IApiResponse<IAssessmentAttemptDetail>, number>({
      query: (attemptId) => ({
        url: APIEndpoints.Recruitment.AssessmentAttemptDetail(attemptId),
      }),
      providesTags: (_r, _e, attemptId) => [{ type: "AssessmentAttempt", id: attemptId }],
    }),

    reviewAssessmentAttempt: builder.mutation<
      IApiResponse<IAssessmentAttempt>,
      { attemptId: number; candidateId: number; body: ISubmitAssessmentReviewPayload }
    >({
      query: ({ attemptId, body }) => ({
        url: APIEndpoints.Recruitment.AssessmentAttemptReview(attemptId),
        method: "POST",
        body,
      }),
      invalidatesTags: (_r, _e, { candidateId }) => [
        { type: "AssessmentAttempt", id: `CANDIDATE_${candidateId}` },
      ],
    }),

    acceptAssessmentAiRecommendation: builder.mutation<
      IApiResponse<IAssessmentAttempt>,
      { attemptId: number; candidateId: number }
    >({
      query: ({ attemptId }) => ({
        url: APIEndpoints.Recruitment.AssessmentAttemptAcceptAi(attemptId),
        method: "POST",
      }),
      invalidatesTags: (_r, _e, { candidateId }) => [
        { type: "AssessmentAttempt", id: `CANDIDATE_${candidateId}` },
      ],
    }),

    /**
     * "Review again": a new AI review of a submitted attempt (#16), queued.
     * Answers at once with `aiReviewStatus: "pending"`; the tab polls the
     * candidate's attempts until it reads done or failed.
     */
    requestAssessmentAiReview: builder.mutation<
      IApiResponse<IAssessmentAttempt>,
      { attemptId: number; candidateId: number; locale?: "en" | "ar" }
    >({
      query: ({ attemptId, locale }) => ({
        url: APIEndpoints.Recruitment.AssessmentAttemptAiReview(attemptId),
        method: "POST",
        body: { locale },
      }),
      invalidatesTags: (_r, _e, { candidateId, attemptId }) => [
        { type: "AssessmentAttempt", id: `CANDIDATE_${candidateId}` },
        { type: "AssessmentAttempt", id: attemptId },
      ],
    }),

    decideAssessmentAttempt: builder.mutation<
      IApiResponse<IAssessmentAttempt>,
      { attemptId: number; candidateId: number; body: IAssessmentFinalDecisionPayload }
    >({
      query: ({ attemptId, body }) => ({
        url: APIEndpoints.Recruitment.AssessmentAttemptDecision(attemptId),
        method: "POST",
        body,
      }),
      invalidatesTags: (_r, _e, { candidateId }) => [
        { type: "AssessmentAttempt", id: `CANDIDATE_${candidateId}` },
      ],
    }),
  }),
});

export const {
  useGetAssessmentsQuery,
  useGetAssessmentCountsQuery,
  useGetAssessmentQuery,
  useCreateAssessmentMutation,
  useUpdateAssessmentMutation,
  useGenerateAssessmentMutation,
  useDuplicateAssessmentMutation,
  useArchiveAssessmentMutation,
  useRestoreAssessmentMutation,
  useGetCandidateAssessmentsQuery,
  useGetAssessmentAttemptDetailQuery,
  useReviewAssessmentAttemptMutation,
  useAcceptAssessmentAiRecommendationMutation,
  useRequestAssessmentAiReviewMutation,
  useDecideAssessmentAttemptMutation,
} = recruitmentAssessmentsApi;
